/*
 * Copyright Mimic Networks, Inc. 2024.
 */

import { PlusOutlined } from '@ant-design/icons';
import { Grid } from 'antd';
import { useNavigate } from 'react-router-dom';

import { CountLabel } from '@/components/CountLabel';
import { GuardedButton } from '@/components/GuardedButton';
import { PageHeader } from '@/components/PageHeader';
import { useMimicTranslation } from '@/hooks/useMimicTranslation';
import { Segmented, SegmentedValue } from '@/primitives/Segmented';
import { useMgmtPlaneStateStore } from '@/state/mgmtPlaneStore';
import './UsersHeader.css';

// eslint-disable-next-line react-refresh/only-export-components
export enum UserState {
  REMOVED = 'removed',
  ACTIVE = 'active',
}

export type UsersHeaderProps = {
  activeFilterValue: SegmentedValue;
  onChangeActiveFilter: (value: SegmentedValue) => void;
  activeTotal: number;
  removedTotal: number;
  isLoading: boolean;
};

const { useBreakpoint } = Grid;

export function UsersHeader({
  activeFilterValue,
  onChangeActiveFilter,
  activeTotal,
  removedTotal,
  isLoading,
}: UsersHeaderProps) {
  const { t } = useMimicTranslation('users');
  const tenantID = useMgmtPlaneStateStore((state) => state.selectedTenantID);
  const navigate = useNavigate();
  const screens = useBreakpoint();

  const openInviteModal = () => {
    navigate(`/tenants/${tenantID}/users/invite`);
  };

  const buttonHeight = screens.sm ? undefined : '45px';
  const buttonWidth = screens.sm ? undefined : '100%';

  return (
    <div className="users-header">
      <div className="users-header-page-header">
        <PageHeader title={t('title')} text={t('subtitle')} />
        <GuardedButton
          requiredRole="admin"
          type="primary"
          onClick={openInviteModal}
          dataTestId="invite-new-user"
          style={{
            height: buttonHeight,
            width: buttonWidth,
          }}
        >
          <PlusOutlined /> {t('newUser')}
        </GuardedButton>
      </div>
      <div className="users-page-segmented-container">
        <Segmented
          dataTestId="removed-switcher"
          defaultValue={UserState.ACTIVE}
          value={activeFilterValue}
          onChange={onChangeActiveFilter}
          options={[
            {
              label: <CountLabel text="Active" count={activeTotal} loading={isLoading} data-testid="segment-active" />,
              value: UserState.ACTIVE,
            },
            {
              label: (
                <CountLabel text="Removed" count={removedTotal} loading={isLoading} data-testid="segment-removed" />
              ),
              value: UserState.REMOVED,
            },
          ]}
          block={!screens.sm}
        />
      </div>
    </div>
  );
}
